import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mapRawLdsSupplementsToMockupExtras } from "../../../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";
import { useFlightRealProposalSubmit } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/context/FlightRealProposalSubmitContext";
import { getAvailableExtras } from "../../unifiedFlightSearch/duffelExtras";
import { buildLiveExtrasCatalog, buildLivePaymentViewModel } from "../bridge/liveStageModels";
import { useLiveFlightStage } from "../bridge/useLiveFlightStage";
import { mapFlightPaymentViewModel } from "./mapFlightPaymentViewModel";
import PaymentScreen from "./PaymentScreen";

/**
 * Live payment step.
 * Real flow creates a draft headless and opens the existing payment screen
 * (installments → Yaad Sarig/HYP). It does not open a Proposal page.
 * Design preview keeps PaymentScreen and submitPayNowFromReview.
 */
export default function PaymentLive() {
  const { booking, search, presentation, onBack } = useLiveFlightStage();
  const {
    useRealFlow,
    ensureOrderForPayment,
    submitBankTransferFromReview,
    submitPayNowFromReview,
    isSubmittingPayNow,
    isSubmittingBankTransfer,
  } = useFlightRealProposalSubmit();
  const submitLock = useRef(false);
  const busy = Boolean(isSubmittingPayNow || isSubmittingBankTransfer);
  const searchFields = search || {};
  const { selectedFlightOffer, extras, passengers } = booking || {};
  const rawDeal = selectedFlightOffer?.rawDeal ?? null;

  const catalog = useMemo(
    () =>
      buildLiveExtrasCatalog({
        roomInfo: searchFields.roomInfo,
        passengers,
        ldsRows: rawDeal ? mapRawLdsSupplementsToMockupExtras(rawDeal) : [],
        duffelExtras: getAvailableExtras(selectedFlightOffer),
        extras,
        baggageLines: presentation.offer?.baggage?.summaryLines || [],
        fare: presentation.offer?.fare || null,
      }),
    [searchFields.roomInfo, passengers, rawDeal, selectedFlightOffer, extras, presentation.offer]
  );

  const flightPaymentVm = useMemo(
    () =>
      mapFlightPaymentViewModel({
        offer: presentation.offer,
        passengers,
        extrasCatalog: catalog,
        price: presentation.priceVm,
      }),
    [presentation.offer, presentation.priceVm, passengers, catalog]
  );

  const payment = useMemo(
    () =>
      buildLivePaymentViewModel({
        offer: presentation.offer,
        holdEligible: false,
        status: busy ? "processing" : null,
      }),
    [presentation.offer, busy]
  );

  const onContinue = ({ actionId } = {}) => {
    if (busy || submitLock.current) return;
    if (actionId === "bank") {
      submitLock.current = true;
      void Promise.resolve(submitBankTransferFromReview()).finally(() => {
        submitLock.current = false;
      });
      return;
    }
    if (actionId === "card") {
      submitLock.current = true;
      void Promise.resolve(submitPayNowFromReview()).finally(() => {
        submitLock.current = false;
      });
    }
  };

  if (useRealFlow) {
    return (
      <FlightPaymentScreenHandoff
        ensureOrderForPayment={ensureOrderForPayment}
        flightPaymentVm={flightPaymentVm}
      />
    );
  }

  return (
    <PaymentScreen
      offer={presentation.offer}
      search={presentation.search}
      payment={payment}
      initialMethodId="bank"
      onContinue={onContinue}
      onBack={onBack}
    />
  );
}

function FlightPaymentScreenHandoff({ ensureOrderForPayment, flightPaymentVm }) {
  const navigate = useNavigate();
  const started = useRef(false);
  const alive = useRef(true);
  const flightPaymentVmRef = useRef(flightPaymentVm);
  flightPaymentVmRef.current = flightPaymentVm;
  const [error, setError] = useState("");

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (started.current) return undefined;
    started.current = true;
    (async () => {
      try {
        const created = await ensureOrderForPayment();
        if (!alive.current) return;
        const orderId = created?.orderId ? String(created.orderId) : "";
        if (!orderId) {
          started.current = false;
          setError("לא ניתן לפתוח את מסך התשלום");
          return;
        }
        navigate(`/p/dashboard/dynamic/profile/${encodeURIComponent(orderId)}`, {
          state: { flightEnginePayment: flightPaymentVmRef.current },
        });
      } catch (err) {
        started.current = false;
        if (alive.current) setError(err?.message || "לא ניתן לפתוח את מסך התשלום");
      }
    })();
    return undefined;
  }, [ensureOrderForPayment, navigate]);

  return <p>{error || "מכינים את מסך התשלום..."}</p>;
}
